import { useState } from 'react';
import { Size, t } from '@bms2/shared';
import { TextInput } from '../../components/controls';
import { Modal } from '../../components/modals/Modal';
import modalStyles from '../../components/modals/modal.module.css';
import { PAGES_PATH, get, post } from '../records/api';

/** One floor no reason covers yet: its breach sentence, and the line it is on or null for the document's own. */
export interface OpenFloor {
  message: string;
  lineId: string | null;
}

/** The floors a Quote's page answer carries, as far as this window reads them (decisions 1629, 1695). */
interface FloorAnswer {
  documentFloor?: { message: string; acknowledgement: unknown } | null;
  lineFloors?: readonly { lineId: string; message: string; acknowledgement: unknown }[];
}

/** 🔒 The floors no reason covers yet, off a Quote's page answer (T1195; decision 1861). */
export function openFloorsOf(answer: FloorAnswer): OpenFloor[] {
  return [
    ...(answer.documentFloor && answer.documentFloor.acknowledgement === null ? [{ message: answer.documentFloor.message, lineId: null }] : []),
    ...(answer.lineFloors ?? []).filter((floor) => floor.acknowledgement === null).map((floor) => ({ message: floor.message, lineId: floor.lineId })),
  ];
}

/**
 * 🔒 The queue reads a Quote's floors with one request, and only once its Approve was refused for the floor
 * (T1195; decision 1861); null where the read fails.
 */
export async function readQuoteFloors(quoteId: string, apiBaseUrl = ''): Promise<OpenFloor[] | null> {
  const answer = await get<FloorAnswer>(`${PAGES_PATH}/Quote/${encodeURIComponent(quoteId)}`, apiBaseUrl);
  return answer.ok ? openFloorsOf(answer.data) : null;
}

/**
 * 🔒 The one window an approver overrides a floor in, on the Quote's page and in
 * the Pending Approvals queue alike (T1195; decision 1861). It shows each breach
 * sentence and takes one reason. Its confirm writes that reason against every
 * open floor and then calls the approval, so the Quote is approved in one act.
 * 🔨 No silent pass: the confirm stays greyed until a reason is typed.
 */
export function FloorOverrideWindow({ floors, acknowledgementPath, apiBaseUrl = '', onApprove, onCancel, onError }: {
  floors: readonly OpenFloor[];
  acknowledgementPath: string;
  apiBaseUrl?: string;
  /** The approval itself, run once every reason is written. */
  onApprove: () => Promise<void>;
  onCancel: () => void;
  onError: (message: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    const typed = reason.trim();
    setBusy(true);
    for (const floor of floors) {
      const written = await post(acknowledgementPath, floor.lineId ? { reason: typed, lineId: floor.lineId } : { reason: typed }, apiBaseUrl);
      if (!written.ok) {
        // The failure is drawn first and the window then closes; closing unmounts it, so nothing sets state afterwards (T1235).
        onError(written.message);
        return onCancel();
      }
    }
    // The approval closes the window itself, so no state is written once it returns (T1235).
    await onApprove();
  };
  return (
    <Modal
      open
      title={t('approvals.floor.title')}
      onCancel={onCancel}
      dataModal="approvalFloorOverride"
      actions={[{ kind: 'primary', label: t('approvals.floor.confirm'), onAct: () => void confirm(), disabled: busy || reason.trim() === '' }, { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel }]}
    >
      {floors.map((floor) => (
        <p key={floor.lineId ?? 'document'} className={modalStyles.fieldLabel} data-approval-floor={floor.lineId ?? 'document'}>{floor.message}</p>
      ))}
      <p className={modalStyles.fieldLabel}>{t('approvals.floor.body')}</p>
      <TextInput size={Size.Large} label={t('approvals.floor.reason')} value={reason} onChange={setReason} required name="approvalFloorReason" />
    </Modal>
  );
}
